/**
 * Country Detection Utility for Dynamic Text
 * Based on the profezac.com implementation
 */
import React from 'react';

// Country mappings with names and flags for Spanish
export const countryMappings = {
  'AR': { nameEs: 'los argentinos', nameEn: 'Argentinians', namePt: 'os argentinos', emoji: '🇦🇷' },
  'MX': { nameEs: 'los mexicanos', nameEn: 'Mexicans', namePt: 'os mexicanos', emoji: '🇲🇽' },
  'CO': { nameEs: 'los colombianos', nameEn: 'Colombians', namePt: 'os colombianos', emoji: '🇨🇴' },
  'PE': { nameEs: 'los peruanos', nameEn: 'Peruvians', namePt: 'os peruanos', emoji: '🇵🇪' },
  'CL': { nameEs: 'los chilenos', nameEn: 'Chileans', namePt: 'os chilenos', emoji: '🇨🇱' },
  'VE': { nameEs: 'los venezolanos', nameEn: 'Venezuelans', namePt: 'os venezuelanos', emoji: '🇻🇪' },
  'EC': { nameEs: 'los ecuatorianos', nameEn: 'Ecuadorians', namePt: 'os equatorianos', emoji: '🇪🇨' },
  'BO': { nameEs: 'los bolivianos', nameEn: 'Bolivians', namePt: 'os bolivianos', emoji: '🇧🇴' },
  'PY': { nameEs: 'los paraguayos', nameEn: 'Paraguayans', namePt: 'os paraguaios', emoji: '🇵🇾' },
  'UY': { nameEs: 'los uruguayos', nameEn: 'Uruguayans', namePt: 'os uruguaios', emoji: '🇺🇾' },
  'CR': { nameEs: 'los costarricenses', nameEn: 'Costa Ricans', namePt: 'os costarriquenhos', emoji: '🇨🇷' },
  'PA': { nameEs: 'los panameños', nameEn: 'Panamanians', namePt: 'os panamenhos', emoji: '🇵🇦' },
  'GT': { nameEs: 'los guatemaltecos', nameEn: 'Guatemalans', namePt: 'os guatemaltecos', emoji: '🇬🇹' },
  'SV': { nameEs: 'los salvadoreños', nameEn: 'Salvadorans', namePt: 'os salvadorenhos', emoji: '🇸🇻' },
  'HN': { nameEs: 'los hondureños', nameEn: 'Hondurans', namePt: 'os hondurenhos', emoji: '🇭🇳' },
  'NI': { nameEs: 'los nicaragüenses', nameEn: 'Nicaraguans', namePt: 'os nicaraguenses', emoji: '🇳🇮' },
  'DO': { nameEs: 'los dominicanos', nameEn: 'Dominicans', namePt: 'os dominicanos', emoji: '🇩🇴' },
  'CU': { nameEs: 'los cubanos', nameEn: 'Cubans', namePt: 'os cubanos', emoji: '🇨🇺' },
  'PR': { nameEs: 'los puertorriqueños', nameEn: 'Puerto Ricans', namePt: 'os porto-riquenhos', emoji: '🇵🇷' },
  'ES': { nameEs: 'los españoles', nameEn: 'Spaniards', namePt: 'os espanhóis', emoji: '🇪🇸' },
  'US': { nameEs: 'los latinos en', nameEn: 'Latinos living in', namePt: 'os latinos que vivem em', emoji: '🇺🇸' },
  'BR': { nameEs: 'los brasileños', nameEn: 'Brazilians', namePt: 'os brasileiros', emoji: '🇧🇷' }
};

/**
 * Detect user's country using IP geolocation
 * @returns {Promise<string>} Country code (e.g., 'US', 'MX')
 */
export const detectUserCountry = async () => {
  try {
    // First try geolocation-db.com (same as profezac.com geo-converter)
    const response = await fetch('https://geolocation-db.com/json/');
    if (response.ok) {
      const data = await response.json();
      return data.country_code || 'US';
    }
  } catch (error) {
    console.log('Geolocation-db failed, trying ipapi.co');
  }

  try {
    // Fallback to ipapi.co (same as profezac.com)
    const response = await fetch('https://ipapi.co/country_code/');
    if (response.ok) {
      const countryCode = await response.text();
      if (countryCode && countryCode.length === 2) {
        return countryCode.toUpperCase();
      }
    }
  } catch (error) {
    console.log('IP detection failed, using default US');
  }

  // Default fallback
  return 'US';
};

/**
 * Get country-specific text and emoji based on detected country and language
 * @param {string} countryCode - Two letter country code
 * @param {string} language - Language code ('es', 'en', 'pt')
 * @returns {object} Object with country name and emoji
 */
export const getCountryText = (countryCode, language = 'es') => {
  const country = countryMappings[countryCode];
  
  if (!country) {
    // Default fallback text based on language
    const defaultText = {
      'es': 'la comunidad latina',
      'en': 'the Latino community', 
      'pt': 'a comunidade latina'
    };
    return {
      name: defaultText[language] || defaultText['es'],
      emoji: '❤️'
    };
  }

  // Get country name in the specified language
  let name;
  switch (language) {
    case 'en':
      name = country.nameEn;
      break;
    case 'pt':
      name = country.namePt;
      break;
    case 'es':
    default:
      name = country.nameEs;
      break;
  }

  return {
    name,
    emoji: country.emoji
  };
};

// Basic gentilicios without articles for specific use cases (like final_cta)
export const basicGentilicios = {
  'AR': { nameEs: 'Argentinos', nameEn: 'Argentinians', namePt: 'Argentinos', emoji: '🇦🇷' },
  'MX': { nameEs: 'Mexicanos', nameEn: 'Mexicans', namePt: 'Mexicanos', emoji: '🇲🇽' },
  'CO': { nameEs: 'Colombianos', nameEn: 'Colombians', namePt: 'Colombianos', emoji: '🇨🇴' },
  'PE': { nameEs: 'Peruanos', nameEn: 'Peruvians', namePt: 'Peruanos', emoji: '🇵🇪' },
  'CL': { nameEs: 'Chilenos', nameEn: 'Chileans', namePt: 'Chilenos', emoji: '🇨🇱' },
  'VE': { nameEs: 'Venezolanos', nameEn: 'Venezuelans', namePt: 'Venezuelanos', emoji: '🇻🇪' },
  'EC': { nameEs: 'Ecuatorianos', nameEn: 'Ecuadorians', namePt: 'Equatorianos', emoji: '🇪🇨' },
  'BO': { nameEs: 'Bolivianos', nameEn: 'Bolivians', namePt: 'Bolivianos', emoji: '🇧🇴' },
  'PY': { nameEs: 'Paraguayos', nameEn: 'Paraguayans', namePt: 'Paraguaios', emoji: '🇵🇾' },
  'UY': { nameEs: 'Uruguayos', nameEn: 'Uruguayans', namePt: 'Uruguaios', emoji: '🇺🇾' },
  'CR': { nameEs: 'Costarricenses', nameEn: 'Costa Ricans', namePt: 'Costarriquenhos', emoji: '🇨🇷' },
  'PA': { nameEs: 'Panameños', nameEn: 'Panamanians', namePt: 'Panamenhos', emoji: '🇵🇦' },
  'GT': { nameEs: 'Guatemaltecos', nameEn: 'Guatemalans', namePt: 'Guatemaltecos', emoji: '🇬🇹' },
  'SV': { nameEs: 'Salvadoreños', nameEn: 'Salvadorans', namePt: 'Salvadorenhos', emoji: '🇸🇻' },
  'HN': { nameEs: 'Hondureños', nameEn: 'Hondurans', namePt: 'Hondurenhos', emoji: '🇭🇳' },
  'NI': { nameEs: 'Nicaragüenses', nameEn: 'Nicaraguans', namePt: 'Nicaraguenses', emoji: '🇳🇮' },
  'DO': { nameEs: 'Dominicanos', nameEn: 'Dominicans', namePt: 'Dominicanos', emoji: '🇩🇴' },
  'CU': { nameEs: 'Cubanos', nameEn: 'Cubans', namePt: 'Cubanos', emoji: '🇨🇺' },
  'PR': { nameEs: 'Puertorriqueños', nameEn: 'Puerto Ricans', namePt: 'Porto-riquenhos', emoji: '🇵🇷' },
  'ES': { nameEs: 'Españoles', nameEn: 'Spaniards', namePt: 'Espanhóis', emoji: '🇪🇸' },
  'US': { nameEs: 'Latinos en', nameEn: 'Latinos living in', namePt: 'Latinos que vivem em', emoji: '🇺🇸' },
  'BR': { nameEs: 'Brasileños', nameEn: 'Brazilians', namePt: 'Brasileiros', emoji: '🇧🇷' }
};

/**
 * Get basic gentilicio (without articles) for specific use cases
 * @param {string} countryCode - Two letter country code
 * @param {string} language - Language code ('es', 'en', 'pt')
 * @returns {object} Object with country name and emoji (without articles)
 */
export const getBasicGentilicio = (countryCode, language = 'es') => {
  const country = basicGentilicios[countryCode];
  
  if (!country) {
    // Default fallback text based on language
    const defaultText = {
      'es': 'Latinos',
      'en': 'Latinos', 
      'pt': 'Latinos'
    };
    return {
      name: defaultText[language] || defaultText['es'],
      emoji: '❤️'
    };
  }

  // Get country name in the specified language
  let name;
  switch (language) {
    case 'en':
      name = country.nameEn;
      break;
    case 'pt':
      name = country.namePt;
      break;
    case 'es':
    default:
      name = country.nameEs;
      break;
  }

  return {
    name,
    emoji: country.emoji
  };
};

/**
 * React hook for country detection and text
 * @param {string} language - Current language ('es', 'en', 'pt')
 * @returns {object} Object with country text, emoji, and loading state
 */
export const useCountryDetection = (language = 'es') => {
  const [countryData, setCountryData] = React.useState({
    name: language === 'es' ? 'la comunidad latina' : 
          language === 'en' ? 'the Latino community' : 'a comunidade latina',
    emoji: '❤️',
    loading: true
  });

  React.useEffect(() => {
    const loadCountryData = async () => {
      try {
        const countryCode = await detectUserCountry();
        const countryText = getCountryText(countryCode, language);
        setCountryData({
          ...countryText,
          loading: false
        });
      } catch (error) {
        console.error('Country detection failed:', error);
        setCountryData(prev => ({ ...prev, loading: false }));
      }
    };

    loadCountryData();
  }, [language]);

  return countryData;
};